import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";

import LevelFilter from "./LevelFilter";
import ActionButtons from "./ActionButtons";
import { UserLevel } from "utils/constant";
import InputRadio from "components/atoms/InputRadio";
import InputDate from "components/atoms/InputDate";
import InputSelect from "components/atoms/InputSelect";
import FlickIdentification from "services/FlickIdentification";
import {
  buildExportRows,
  parseFasyankesCodes,
  filterLarvaByFasyankes,
  getDesaFilterOptions,
  getTpFilterOptions,
} from "../utils";

const FlickIdentificationService = new FlickIdentification();

const isValidYYYY = (v) => v !== undefined && /^\d{4}$/.test(String(v));

const FilterSection = ({
  currentUser,
  selectedLevel,
  setSelectedLevel,
  levelData,
  levelDataOptions,
  setLevelIdValue,
  selectedProvince,
  setSelectedProvince,
  selectedDistrict,
  setSelectedDistrict,
  selectedFasyankes,
  setSelectedFasyankes,
  handleApplyFilter,
  tableRef,
  showData,
  showFilter,
  setShowFilter,
  levelIdValue,
  reportName = "LAPORAN_TREN2_PEMANTAUAN_JENTIK",
}) => {
  const [year, setYear] = useState("");
  const [larvaIdentifications, setLarvaIdentifications] = useState([]);
  const [selectedDesa, setSelectedDesa] = useState(""); // urbanvillage CODE
  const [selectedNamaTp, setSelectedNamaTp] = useState("");

  const selectedDesaRef = useRef(selectedDesa);
  selectedDesaRef.current = selectedDesa;
  const selectedNamaTpRef = useRef(selectedNamaTp);
  selectedNamaTpRef.current = selectedNamaTp;

  useEffect(() => {
    let isCancelled = false;

    // Load breeding-place identifications for all selected fasyankes so the
    // Desa and TP-name filters can be chosen before applying the report.
    const loadOptions = async () => {
      if (selectedLevel !== UserLevel.Fasyankes || !selectedFasyankes) {
        setLarvaIdentifications([]);
        setSelectedDesa("");
        setSelectedNamaTp("");
        return;
      }

      const fasyankesCodes = parseFasyankesCodes(selectedFasyankes);
      if (fasyankesCodes.length === 0) {
        setLarvaIdentifications([]);
        setSelectedDesa("");
        setSelectedNamaTp("");
        return;
      }

      let identifications = [];
      try {
        const resp = await FlickIdentificationService.getAll({
          page: 1,
          limit: 9999,
          fasyankes_ids: fasyankesCodes.join(","),
        });
        identifications = filterLarvaByFasyankes(
          resp?.vector_larva_identifications || [],
          fasyankesCodes
        );
      } catch (err) {
        console.error(err);
        identifications = [];
      }

      if (isCancelled) return;

      setLarvaIdentifications(identifications);

      // Keep selectedDesa and selectedNamaTp if they still exist in the new option lists;
      // clear them otherwise.
      const newDesaOptions = getDesaFilterOptions(identifications);
      const currentDesa = selectedDesaRef.current;
      const nextDesa =
        currentDesa && newDesaOptions.some((o) => o.value === currentDesa)
          ? currentDesa
          : "";
      if (nextDesa !== currentDesa) {
        setSelectedDesa(nextDesa);
      }

      const newTpOptions = getTpFilterOptions(identifications, nextDesa);
      const currentNamaTp = selectedNamaTpRef.current;
      const nextNamaTp =
        currentNamaTp && newTpOptions.some((o) => o.value === currentNamaTp)
          ? currentNamaTp
          : "";
      if (nextNamaTp !== currentNamaTp) {
        setSelectedNamaTp(nextNamaTp);
      }
    };

    loadOptions();

    return () => {
      isCancelled = true;
    };
  }, [selectedFasyankes, selectedLevel]);

  useEffect(() => {
    if (currentUser.level === UserLevel.Fasyankes) {
      setLevelIdValue(currentUser?.fasyankes_id);
    } else if (currentUser.level === UserLevel.District) {
      setSelectedFasyankes("");
      setLevelIdValue(currentUser?.district_id);
    } else if (currentUser.level === UserLevel.Province) {
      setSelectedDistrict("");
      setSelectedFasyankes("");
      setLevelIdValue(currentUser?.province_id);
    } else if (currentUser.level === UserLevel.National) {
      setSelectedProvince("");
      setSelectedDistrict("");
      setSelectedFasyankes("");
    } else {
      setLevelIdValue("");
    }
  }, [selectedLevel]);

  const levelLabel = useMemo(
    () =>
      selectedLevel === UserLevel.National
        ? UserLevel.National
        : levelIdValue?.split("|")[1],
    [selectedLevel, levelIdValue]
  );

  const isLevelChosen = () =>
    (selectedLevel === UserLevel.Fasyankes && !!selectedFasyankes) ||
    (selectedLevel === UserLevel.District && !!selectedDistrict) ||
    (selectedLevel === UserLevel.Province && !!selectedProvince) ||
    selectedLevel === UserLevel.National;

  const isPeriodValid = () => isValidYYYY(year);

  const isEnableButton = () => isLevelChosen() && isPeriodValid();

  const buildPayload = () => {
    const payload = {
      level: selectedLevel,
      period: "year",
      year: Number(year),
    };

    if (selectedLevel === UserLevel.Fasyankes)
      payload.level_id = selectedFasyankes;
    if (selectedLevel === UserLevel.District)
      payload.level_id = selectedDistrict;
    if (selectedLevel === UserLevel.Province)
      payload.level_id = selectedProvince;

    // Server-side desa + breeding-place filters (only meaningful at fasyankes level).
    if (selectedLevel === UserLevel.Fasyankes) {
      if (selectedDesa) payload.urbanvillage = selectedDesa;
      if (selectedNamaTp) payload.nama_tp = selectedNamaTp;
    }

    return payload;
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(buildExportRows(tableRef.current)),
      "Sheet 1"
    );

    const periodSuffix = `Y${year}`;

    XLSX.writeFile(
      wb,
      `${reportName}_${levelLabel}_${periodSuffix}_${moment().format(
        "DDMMYYYY"
      )}.xlsx`
    );
  };

  // Distinct desa options from the loaded identifications (value = urbanvillage code).
  const desaFilterOptions = useMemo(
    () => getDesaFilterOptions(larvaIdentifications),
    [larvaIdentifications]
  );

  // TP-name options, narrowed to the selected desa when one is chosen.
  const tpFilterOptions = useMemo(
    () => getTpFilterOptions(larvaIdentifications, selectedDesa),
    [larvaIdentifications, selectedDesa]
  );

  return (
    <div className="filter">
      <div className="filter-header">
        <p>FILTER</p>
        <button
          className="btn_toggle_filter"
          onClick={() => setShowFilter(!showFilter)}
        >
          {showFilter ? <span>&#9650;</span> : <span>&#9660;</span>}
        </button>
      </div>

      {showFilter && (
        <div className="filter-body">
          <div className="filter-item">
            <div className="filter-label">
              <p>Periode</p>
            </div>
            <div className="filter-field">
              <InputRadio
                isRequired={true}
                options={[{ label: "Tahunan", value: "year" }]}
                onChange={() => {}}
                selectedOption="year"
              />

              <div style={{ marginTop: 12 }}>
                <InputDate
                  value={year ? moment(String(year), "YYYY") : null}
                  onChange={(e) => {
                    const selectedYear = moment(e).format("YYYY");
                    setYear(Number(selectedYear));
                  }}
                  placeholder={"Pilih Tahun"}
                  dateFormat={"YYYY"}
                />
              </div>
            </div>
          </div>

          <div className="filter-item">
            <div className="filter-label">
              <p>Level</p>
            </div>
            <div className="filter-field">
              <LevelFilter
                currentUser={currentUser}
                selectedLevel={selectedLevel}
                setSelectedLevel={setSelectedLevel}
                levelData={levelData}
                levelDataOptions={levelDataOptions}
                setLevelIdValue={setLevelIdValue}
                selectedProvince={selectedProvince}
                setSelectedProvince={setSelectedProvince}
                selectedDistrict={selectedDistrict}
                setSelectedDistrict={setSelectedDistrict}
                selectedFasyankes={selectedFasyankes}
                setSelectedFasyankes={setSelectedFasyankes}
              />
            </div>
          </div>

          {selectedLevel === UserLevel.Fasyankes && selectedFasyankes && (
            <div className="filter-item">
              <div className="filter-label">
                <p>Desa & Tempat Perindukan</p>
              </div>
              <div className="filter-field">
                <div style={{ display: "flex", flexDirection: "row", gap: "1.2rem" }}>
                  <div style={{ flex: "1" }}>
                    <InputSelect
                      label="Desa"
                      placeholder="Semua Desa"
                      isClearable
                      data={desaFilterOptions}
                      value={
                        desaFilterOptions.find((o) => o.value === selectedDesa) || ""
                      }
                      onChange={(e) => {
                        // Changing desa resets the dependent TP-name filter.
                        setSelectedDesa(e ? e.value : "");
                        setSelectedNamaTp("");
                      }}
                    />
                  </div>
                  <div style={{ flex: "1" }}>
                    <InputSelect
                      label="Nama Tempat Perindukan"
                      placeholder="Semua Tempat Perindukan"
                      isClearable
                      data={tpFilterOptions}
                      value={
                        tpFilterOptions.find((o) => o.value === selectedNamaTp) || ""
                      }
                      onChange={(e) => setSelectedNamaTp(e ? e.value : "")}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="filter-item">
            <div className="filter-field">
              <ActionButtons
                onApply={() => handleApplyFilter(buildPayload())}
                onExport={exportToExcel}
                isExportVisible={showData}
                isDisabled={!isEnableButton()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterSection;
